import React, { useRef, useState, useCallback, useLayoutEffect } from "react";

// Draggable before/after comparison slider. beforeSrc shown underneath, afterSrc clipped on top.
export const PhotoCompare = ({ beforeSrc, afterSrc, beforeLabel = "Before", afterLabel = "After" }) => {
  const [pos, setPos] = useState(50);
  const [cw, setCw] = useState(0);
  const ref = useRef(null);
  const dragging = useRef(false);

  useLayoutEffect(() => {
    const measure = () => { if (ref.current) setCw(ref.current.getBoundingClientRect().width); };
    measure();
    const ro = new ResizeObserver(measure);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onDown = (e) => { dragging.current = true; move(e.touches ? e.touches[0].clientX : e.clientX); };
  const onMove = (e) => { if (dragging.current) move(e.touches ? e.touches[0].clientX : e.clientX); };
  const onUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      className="relative w-full aspect-square max-h-[420px] rounded-xl overflow-hidden border border-border select-none touch-none bg-black/5"
      data-testid="photo-compare"
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      <img src={beforeSrc} alt="before" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500 text-white z-20">{beforeLabel}</span>

      <div className="absolute inset-y-0 left-0 overflow-hidden z-10" style={{ width: `${pos}%` }}>
        <img
          src={afterSrc}
          alt="after"
          className="absolute inset-y-0 left-0 h-full object-cover"
          style={{ width: cw ? `${cw}px` : "100%", maxWidth: "none" }}
          draggable={false}
        />
        <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500 text-white">{afterLabel}</span>
      </div>

      <div className="absolute inset-y-0 z-30" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-0.5 h-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.25)]" />
        <button
          onMouseDown={onDown}
          onTouchStart={onDown}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white shadow-lg flex items-center justify-center cursor-ew-resize"
          data-testid="photo-compare-handle"
          aria-label="Drag to compare"
        >
          <span className="text-xs font-bold text-primary">⟺</span>
        </button>
      </div>
    </div>
  );
};
